import { useState, useEffect, useRef, useCallback } from "react";
import { OutlookCalendarService } from "../../services/OutlookCalendarService";
import { AppointmentDetails } from "../../types";
import { AppError } from "../../errors/AppError";

interface UseAppointmentDetailsResult {
  details: AppointmentDetails | null;
  loading: boolean;
  error: string | null;
}

export function useAppointmentDetails(): UseAppointmentDetailsResult {
  const [details, setDetails] = useState<AppointmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isInitializedRef = useRef(false);
  const handlersRegisteredRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number>(0);

  const updateAppointmentDetails = useCallback(async (): Promise<void> => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateRef.current;
    
    if (timeSinceLastUpdate < 500) {
      return;
    }

    lastUpdateRef.current = now;

    try {
      const appointmentDetails = await OutlookCalendarService.getAppointmentDetails();
      setDetails(appointmentDetails);
      setError(null);
    } catch {
      // Silently ignore — full error handling is in the effect below
    }
  }, []);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }

    const loadAppointmentDetails = async (): Promise<void> => {
      try {
        const appointmentDetails = await OutlookCalendarService.getAppointmentDetails();
        setDetails(appointmentDetails);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof AppError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load appointment details";

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void Office.onReady(() => {
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        void loadAppointmentDetails();
      }
    });
  }, []);

  useEffect(() => {
    if (!isInitializedRef.current || handlersRegisteredRef.current || loading) {
      return;
    }

    void Office.onReady(() => {
      const item = Office.context.mailbox?.item;
      if (!item) {
        return;
      }

      const isCompose = typeof (item as Office.AppointmentCompose).subject?.getAsync === "function";
      
      if (!isCompose) {
        return;
      }

      handlersRegisteredRef.current = true;

      try {
        Office.context.mailbox.addHandlerAsync(
          Office.EventType.AppointmentTimeChanged,
          () => {
            void updateAppointmentDetails();
          },
          (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.warn("Failed to register time changed handler:", result.error?.message);
            }
          }
        );
      } catch (err) {
        console.warn("Could not register attendees changed handler:", err);
      }

      try {
        Office.context.mailbox.addHandlerAsync(
          Office.EventType.AppointmentTimeChanged,
          () => {
            void updateAppointmentDetails();
          },
          (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
              console.warn("Failed to register time changed handler:", result.error?.message);
            }
          }
        );
      } catch (err) {
        console.warn("Could not register time changed handler:", err);
      }
    });
  }, [loading, updateAppointmentDetails]);

  useEffect(() => {
    if (loading || !details) {
      return;
    }

    let isVisible = true;
    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const pollInterval = setInterval(() => {
      if (isVisible) {
        void updateAppointmentDetails();
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [loading, details, updateAppointmentDetails]);

  return { details, loading, error };
}
